import {naturalSort, range} from '@benchling/js-utils';
import produce from 'immer';

import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {Measurement, MeasurementUnitFragment} from 'constants/MeasurementConstants';
import {readFragmentFromCache} from 'graphql-client/ApolloWrappers';
import {Cell} from 'notebook/tables/TableConstants';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  ContentGroup,
  PlateMap,
  PlateMaterialInput,
  ReagentAssignmentGroup,
  ReagentType,
  Source,
} from 'samples/plate-maps/PlateMapConstants';
import {getGroupIdsForSource} from 'samples/plate-maps/PlateMapUtil';
import {
  FillPlateTransferTableViewMode,
  MULTIPLE_VALUES_SENTINEL,
  MaybeMixedMeasurement,
  MaybeMixedMeasurementUnit,
  MaybeMixedMeasurementValue,
  MaybeMixedPlateMaterialInputId,
  MaybeMixedValue,
  ReagentData,
  ReagentDataBySchemas,
  ReagentMeasurementCellsBySchemas,
  SchemaData,
  SummaryViewRowContents,
  SummaryViewTransferTableColumnKey,
} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import {
  assignMaterialInputsToWells,
  coerceNumericValue,
  getReagents,
  getSharedRow,
  isMultipleValuesCell,
  getMaterialInputId,
  isChangedMaterialInput,
  getEmptyWellSharedRow,
} from 'samples/plate-maps/transfer-table/FillPlateTransferTableUtil';
import {
  MaybeMixedMeasurementWithErrors,
  MeasurementValidationErrors,
  MULTIPLE_SUMMARY_ERRORS,
  ReagentValueValidationError,
  SUM_OF_TRANSFER_QUANTITIES_EXCEEDS_TOTAL_QUANTITY_ERROR_MESSAGE,
} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationConstants';
import {
  getDefaultMeasurementValidationErrors,
  getDefaultMeasurementWithErrors,
  getSumOfTransferQuantitiesExceedsFinalQuantityErrorMessage,
} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationUtil';
import {PLATE_MAP_ERROR_LEVEL_PRECEDENCE} from 'samples/plate-maps/validation/ValidationTypes';
import APIUtil from 'util/APIUtil';

const defaultIsEqual = <V>(a: V, b: V): boolean => (a ?? null) === (b ?? null);
const unitsIsEqual = (
  a: MaybeMixedValue<MeasurementUnit> | undefined,
  b: MaybeMixedValue<MeasurementUnit> | undefined
): boolean => {
  if (a && a.hasOwnProperty('symbol') && b && b.hasOwnProperty('symbol')) {
    return (a as MeasurementUnit).symbol === (b as MeasurementUnit).symbol;
  }
  return defaultIsEqual(a, b);
};

export function getEmptySummaryViewRow({
  showMaterialInputColumn,
}: {
  showMaterialInputColumn: boolean;
}): SummaryViewRowContents {
  return {
    ...getEmptyWellSharedRow({showMaterialInputColumn, viewMode: FillPlateTransferTableViewMode.SUMMARY}),
    schemaGroupingId: '',
    [SummaryViewTransferTableColumnKey.SOURCE_SCHEMA]: {
      text: '',
      typedValue: {
        type: BaseTypedValueType.JSON,
        jsonValue: {apiId: null, schemaColor: null, displayNameOverride: ''},
      },
    },
  };
}

/**
 * For a grouping, if some value has already collated, return either the shared value of all seen items or
 * 'Multiple values' if the given value of the next item in the grouping is different from the collated value.
 * Exported for testing.
 */
export function getCollatedCellValue<T extends MeasurementUnit | number | string | null>(
  seen: boolean,
  collatedValue: MaybeMixedValue<T> | undefined,
  value: MaybeMixedValue<T> | undefined,
  isEqual: (a: MaybeMixedValue<T> | undefined, b: MaybeMixedValue<T> | undefined) => boolean = defaultIsEqual
): MaybeMixedValue<T> {
  if (seen && !isEqual(collatedValue, value)) {
    return MULTIPLE_VALUES_SENTINEL;
  } else {
    return value ?? null;
  }
}

export function getCollatedCellError(
  collatedError: ReagentValueValidationError | null,
  error: ReagentValueValidationError | null
): ReagentValueValidationError | null {
  // Error vs null does not require resolution in well view, but two different errors does.
  if (collatedError == null && error == null) {
    return null;
  } else if (collatedError != null && error == null) {
    return collatedError;
  } else if (collatedError == null && error != null) {
    return error;
  }
  if (
    collatedError!.message !== error!.message &&
    !(
      // Special case: we append & increment a well count to this error as we go; don't punt user to well view
      (
        collatedError!.message.startsWith(SUM_OF_TRANSFER_QUANTITIES_EXCEEDS_TOTAL_QUANTITY_ERROR_MESSAGE) &&
        error!.message.startsWith(SUM_OF_TRANSFER_QUANTITIES_EXCEEDS_TOTAL_QUANTITY_ERROR_MESSAGE)
      )
    )
  ) {
    // If the errors have different levels, show whichever one is more severe.
    let level;
    if (
      PLATE_MAP_ERROR_LEVEL_PRECEDENCE[collatedError!.level] >= PLATE_MAP_ERROR_LEVEL_PRECEDENCE[error!.level]
    ) {
      level = collatedError!.level;
    } else {
      level = error!.level;
    }
    return {message: MULTIPLE_SUMMARY_ERRORS, level};
  } else {
    return error;
  }
}

/**
 * Get a string key to organize well contents and sources.
 * @param reagent Either the content existing in the well or the incoming source
 */
export function getSchemaGroupingId(reagent: ContentGroup | Source): string {
  if (reagent.type === ReagentType.WELL) {
    return `${reagent.type}-${naturalSort(reagent.contents.map((c) => c.groupId)).join('-')}`;
  } else {
    return `${reagent.type}-${naturalSort([...getGroupIdsForSource(reagent)]).join('-')}`;
  }
}

export function getReagentDataBySchemas(
  plateMap: PlateMap,
  isInProcedure: boolean,
  selectedWellIndices: Set<number>
): ReagentDataBySchemas {
  const reagentDataBySchemaId: ReagentDataBySchemas = {};
  const indicesToInclude =
    selectedWellIndices.size > 0 ? selectedWellIndices : new Set(range(0, plateMap.wells.length));
  // Special case: For errors about transfer quantities exceeding total well volume, we want to tack on a
  // message about how many wells have this issue.
  const tooMuchTransferQuantityErrorWellIdsBySchemaGroupingId: {[schemaGroupingId: string]: Set<number>} = {};
  for (const [wellIndex, well] of plateMap.wells.entries()) {
    // If the well isn't selected, don't include it in the roll-up
    if (!indicesToInclude.has(wellIndex)) {
      continue;
    }
    for (const reagent of getReagents(well)) {
      let schemaGroupingSeen = true;
      const schemaGroupingId = getSchemaGroupingId(reagent);
      if (reagentDataBySchemaId[schemaGroupingId] == null) {
        reagentDataBySchemaId[schemaGroupingId] = {
          transferQuantity: getDefaultMeasurementWithErrors(),
          schemas: {},
          type: reagent.type,
        };
        schemaGroupingSeen = false;
      }
      // If we've already explicitly collated some data for this schema grouping and we then find something
      // different, we override this and all future values with 'Multiple values'.
      reagentDataBySchemaId[schemaGroupingId].transferQuantity.measurement.value =
        getCollatedCellValue<MaybeMixedMeasurementValue>(
          schemaGroupingSeen,
          reagentDataBySchemaId[schemaGroupingId].transferQuantity.measurement.value,
          reagent.type === ReagentType.WELL
            ? reagent.quantity.value
            : reagent.transferQuantity.measurement.value
        );
      let transferQuantityError = getCollatedCellError(
        reagentDataBySchemaId[schemaGroupingId].transferQuantity.errors.valueError,
        reagent.type === ReagentType.WELL ? null : reagent.transferQuantity.errors.valueError
      );
      if (
        transferQuantityError != null &&
        transferQuantityError.message.startsWith(
          SUM_OF_TRANSFER_QUANTITIES_EXCEEDS_TOTAL_QUANTITY_ERROR_MESSAGE
        )
      ) {
        tooMuchTransferQuantityErrorWellIdsBySchemaGroupingId[schemaGroupingId] ??= new Set();
        tooMuchTransferQuantityErrorWellIdsBySchemaGroupingId[schemaGroupingId].add(wellIndex);
        transferQuantityError = {
          message: getSumOfTransferQuantitiesExceedsFinalQuantityErrorMessage(
            tooMuchTransferQuantityErrorWellIdsBySchemaGroupingId[schemaGroupingId].size
          ),
          level: transferQuantityError.level,
        };
      }
      reagentDataBySchemaId[schemaGroupingId].transferQuantity.errors.valueError = transferQuantityError;
      reagentDataBySchemaId[schemaGroupingId].transferQuantity.measurement.unit =
        getCollatedCellValue<MaybeMixedMeasurementUnit>(
          schemaGroupingSeen,
          reagentDataBySchemaId[schemaGroupingId].transferQuantity.measurement.unit,
          reagent.type === ReagentType.WELL
            ? reagent.quantity.unit
            : reagent.transferQuantity.measurement.unit,
          unitsIsEqual
        );
      reagentDataBySchemaId[schemaGroupingId].transferQuantity.errors.unitsError = getCollatedCellError(
        reagentDataBySchemaId[schemaGroupingId].transferQuantity.errors.unitsError,
        reagent.type === ReagentType.WELL ? null : reagent.transferQuantity.errors.unitsError
      );

      const leafs =
        reagent.type === ReagentType.SOURCE_CONTAINABLE ||
        (reagent.type === ReagentType.SOURCE_CONTAINER && reagent.contents.length === 0)
          ? [reagent]
          : reagent.contents;
      for (const leaf of leafs) {
        let schemaSeen = true;
        if (reagentDataBySchemaId[schemaGroupingId].schemas[leaf.groupId] == null) {
          reagentDataBySchemaId[schemaGroupingId].schemas[leaf.groupId] = {
            entityIds: new Set(),
            sourceConcentration: getDefaultMeasurementWithErrors(),
            finalConcentration: getDefaultMeasurementWithErrors(),
          };
          if (leaf.type !== ReagentType.SOURCE_CONTAINER && isInProcedure) {
            reagentDataBySchemaId[schemaGroupingId].schemas[leaf.groupId].materialInput = {
              materialInputId: leaf.materialInput!.materialInputId,
              error: null,
            };
          }
          schemaSeen = false;
        }
        reagentDataBySchemaId[schemaGroupingId].schemas[leaf.groupId].entityIds.add(leaf.id);

        // Container reagents don't have concentrations or material inputs, so their rows can remain null.
        if (leaf.type === ReagentType.SOURCE_CONTAINER) {
          continue;
        }

        // If we've already explicitly collated some data for this schema within this schema grouping and then
        // we find something different, we override this and all future values with 'Multiple values'.
        const leafDataForGroup = reagentDataBySchemaId[schemaGroupingId].schemas[leaf.groupId];
        if (isInProcedure) {
          reagentDataBySchemaId[schemaGroupingId].schemas[leaf.groupId].materialInput!.materialInputId =
            getCollatedCellValue<MaybeMixedPlateMaterialInputId>(
              schemaSeen,
              leafDataForGroup.materialInput!.materialInputId,
              leaf.materialInput!.materialInputId
            );
          reagentDataBySchemaId[schemaGroupingId].schemas[leaf.groupId].materialInput!.error =
            getCollatedCellError(leafDataForGroup.materialInput!.error, leaf.materialInput!.error);
        }
        reagentDataBySchemaId[schemaGroupingId].schemas[leaf.groupId].sourceConcentration.measurement.value =
          getCollatedCellValue<MaybeMixedMeasurementValue>(
            schemaSeen,
            leafDataForGroup.sourceConcentration.measurement.value,
            leaf.type === ReagentType.WELL_CONTENT
              ? leaf.initialConcentration?.value
              : leaf.sourceConcentration.measurement.value
          );
        reagentDataBySchemaId[schemaGroupingId].schemas[leaf.groupId].sourceConcentration.errors.valueError =
          getCollatedCellError(
            leafDataForGroup.sourceConcentration.errors.valueError,
            leaf.type === ReagentType.WELL_CONTENT ? null : leaf.sourceConcentration.errors.valueError
          );
        reagentDataBySchemaId[schemaGroupingId].schemas[leaf.groupId].sourceConcentration.measurement.unit =
          getCollatedCellValue<MaybeMixedMeasurementUnit>(
            schemaSeen,
            leafDataForGroup.sourceConcentration.measurement.unit,
            leaf.type === ReagentType.WELL_CONTENT
              ? leaf.initialConcentration?.unit
              : leaf.sourceConcentration.measurement.unit,
            unitsIsEqual
          );
        reagentDataBySchemaId[schemaGroupingId].schemas[leaf.groupId].sourceConcentration.errors.unitsError =
          getCollatedCellError(
            leafDataForGroup.sourceConcentration.errors.unitsError,
            leaf.type === ReagentType.WELL_CONTENT ? null : leaf.sourceConcentration.errors.unitsError
          );
        reagentDataBySchemaId[schemaGroupingId].schemas[leaf.groupId].finalConcentration.measurement.value =
          getCollatedCellValue<MaybeMixedMeasurementValue>(
            schemaSeen,
            leafDataForGroup.finalConcentration.measurement.value,
            leaf.finalConcentration.measurement.value
          );
        reagentDataBySchemaId[schemaGroupingId].schemas[leaf.groupId].finalConcentration.errors.valueError =
          getCollatedCellError(
            leafDataForGroup.finalConcentration.errors.valueError,
            leaf.finalConcentration.errors.valueError
          );
        reagentDataBySchemaId[schemaGroupingId].schemas[leaf.groupId].finalConcentration.measurement.unit =
          getCollatedCellValue<MaybeMixedMeasurementUnit>(
            schemaSeen,
            leafDataForGroup.finalConcentration.measurement.unit,
            leaf.finalConcentration.measurement.unit,
            unitsIsEqual
          );
        reagentDataBySchemaId[schemaGroupingId].schemas[leaf.groupId].finalConcentration.errors.unitsError =
          getCollatedCellError(
            leafDataForGroup.finalConcentration.errors.unitsError,
            leaf.finalConcentration.errors.unitsError
          );
      }
    }
  }

  return reagentDataBySchemaId;
}

/**
 * Updates the reagents in the plate map wells with the given measurement values.
 * @param plateMap The plate map to update
 * @param selectedWellIndices What indices in the plate are currently selected
 * @param reagentMeasurementCellsBySchemas A mapping from schema grouping id to measurement values
 */
export function updateReagentMeasurementCellsBySchemas(
  plateMap: PlateMap,
  selectedWellIndices: Set<number>,
  reagentMeasurementCellsBySchemas: ReagentMeasurementCellsBySchemas
): PlateMap {
  const changedMaterialInputsByEntityId: {[entityId: string]: Set<string>} = {};
  const updatedPlateMap = produce(plateMap, (draft) => {
    for (const [wellIndex, well] of draft.wells.entries()) {
      if (selectedWellIndices.size > 0 && !selectedWellIndices.has(wellIndex)) {
        continue;
      }
      for (const reagent of getReagents(well)) {
        const schemaGroupingId = getSchemaGroupingId(reagent);
        const {transferQuantity, transferQuantityUnit, schemas} =
          reagentMeasurementCellsBySchemas[schemaGroupingId];
        // If we see any Multiple Values cells, do not update the corresponding reagent value.
        if (reagent.type !== ReagentType.WELL) {
          reagent.transferQuantity = {
            measurement: clobberNonMixedValuesInMeasurement(
              getMaybeMixedMeasurementFromCells(transferQuantity, transferQuantityUnit),
              reagent.transferQuantity.measurement
            ),
            errors: getDefaultMeasurementValidationErrors(),
          };
        }
        if (reagent.type === ReagentType.SOURCE_CONTAINABLE) {
          const {
            finalConcentration,
            finalConcentrationUnit,
            materialInput,
            sourceConcentration,
            sourceConcentrationUnit,
          } = schemas[reagent.groupId];
          reagent.finalConcentration = {
            measurement: clobberNonMixedValuesInMeasurement(
              getMaybeMixedMeasurementFromCells(finalConcentration, finalConcentrationUnit),
              reagent.finalConcentration.measurement
            ),
            errors: getDefaultMeasurementValidationErrors(),
          };
          reagent.sourceConcentration = {
            measurement: clobberNonMixedValuesInMeasurement(
              getMaybeMixedMeasurementFromCells(sourceConcentration, sourceConcentrationUnit),
              reagent.sourceConcentration.measurement
            ),
            errors: getDefaultMeasurementValidationErrors(),
          };
          const materialInputId = getMaterialInputId(materialInput);
          if (materialInputId != null) {
            if (isChangedMaterialInput(materialInputId, reagent)) {
              changedMaterialInputsByEntityId[reagent.id] ??= new Set();
              changedMaterialInputsByEntityId[reagent.id].add(materialInputId);
            }
            reagent.materialInput = {materialInputId, error: null};
          }
        } else {
          for (const content of reagent.contents) {
            const {finalConcentration, finalConcentrationUnit, materialInput} = schemas[content.groupId];
            content.finalConcentration = {
              measurement: clobberNonMixedValuesInMeasurement(
                getMaybeMixedMeasurementFromCells(finalConcentration, finalConcentrationUnit),
                content.finalConcentration.measurement
              ),
              errors: getDefaultMeasurementValidationErrors(),
            };
            const materialInputId = getMaterialInputId(materialInput);
            if (materialInputId != null) {
              if (isChangedMaterialInput(materialInputId, content)) {
                changedMaterialInputsByEntityId[content.id] ??= new Set();
                changedMaterialInputsByEntityId[content.id].add(materialInputId);
              }
              content.materialInput = {materialInputId, error: null};
            }
          }
        }
      }
    }
  });
  return assignMaterialInputsToWells(updatedPlateMap, changedMaterialInputsByEntityId);
}

/**
 * Gets the value of each cell in a single row in Summary View.
 * @param assignmentGroup The assignment group to which the reagent belongs
 * @param plateMaterialInputs In a procedure context, a list of plate-used material inputs.
 * @param reagentData Data about starting and final concentrations of a schema
 * @param schemaData Data about what quantity of this schema is being transferred (or is already in the well)
 * @param schemaGroupingId A deduped, sorted, concatenated string of tag schema API IDs that co-occur in the
 *                         same container
 * @param schemaId The API ID of the schema this row represents
 * @param setContainerMergeSpan Whether this row represents the first schema in a container
 * @param showMaterialInputColumn Whether to include a column for configuring material input associations
 */
export function getSummaryViewRow({
  assignmentGroup,
  plateMaterialInputs,
  reagentData,
  schemaData,
  schemaGroupingId,
  schemaId,
  setContainerMergeSpan,
  showMaterialInputColumn,
}: {
  assignmentGroup: ReagentAssignmentGroup;
  plateMaterialInputs?: Array<PlateMaterialInput>;
  showMaterialInputColumn: boolean;
  reagentData: ReagentData;
  schemaData: SchemaData;
  schemaGroupingId: string;
  schemaId: string;
  setContainerMergeSpan: boolean;
}): SummaryViewRowContents {
  const schemasCount = Object.keys(reagentData.schemas).length;
  const isEmptyContainer = schemasCount === 1 && APIUtil.isContainerSchema(schemaId);
  const {transferQuantity} = reagentData;
  const {materialInput, sourceConcentration, finalConcentration} = schemaData;

  const sharedContents = getSharedRow({
    assignmentGroup,
    containerMergeSpan: setContainerMergeSpan ? schemasCount : null,
    finalConcentration,
    isEmptyContainer,
    materialInput: materialInput ?? null,
    plateMaterialInputs,
    reagentType: reagentData.type,
    showMaterialInputColumn,
    sourceConcentration,
    sourceId: schemaId,
    transferQuantity,
    viewMode: FillPlateTransferTableViewMode.SUMMARY,
  });

  return {
    ...sharedContents,
    [SummaryViewTransferTableColumnKey.SOURCE_SCHEMA]: {
      text: '',
      typedValue: {
        type: BaseTypedValueType.JSON,
        jsonValue: {
          displayNameOverride: `${assignmentGroup.name} (${schemaData.entityIds.size})`,
          apiId: schemaId,
          schemaColor: assignmentGroup.color,
        },
      },
    },
    schemaGroupingId,
  };
}

/**
 * Gets what the value of each cell in each row should be in Summary View.
 * @param plateMaterialInputs In a procedure context, a list of plate-used material inputs.
 * @param reagentGroupsById The reagents schemas present in the plate map
 * @param reagentDataBySchemas A map from 'schema grouping' (a deduped, sorted, concatenated string of
 * tag schema API ids that co-occur in the same container) to 1) transfer quantity data, which is stored per
 * container, and 2) for each tag schema API id in the container, a set of reagents with that schema ID in
 * that schema grouping.
 * @param showMaterialInputColumn Whether to include a column for configuring material input associations
 */
export function getSummaryViewRowsContents({
  plateMaterialInputs,
  reagentGroupsById,
  reagentDataBySchemas,
  showMaterialInputColumn,
}: {
  plateMaterialInputs?: Array<PlateMaterialInput>;
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  reagentDataBySchemas: ReagentDataBySchemas;
  showMaterialInputColumn: boolean;
}): Array<SummaryViewRowContents> {
  if (Object.keys(reagentDataBySchemas).length === 0) {
    return [getEmptySummaryViewRow({showMaterialInputColumn})];
  }

  const rowsContents: Array<SummaryViewRowContents> = [];
  for (const [schemaGroupingId, reagentData] of Object.entries(reagentDataBySchemas)) {
    let groupIndex = 0;
    for (const [schemaId, schemaData] of Object.entries(reagentData.schemas)) {
      const assignmentGroup = reagentGroupsById[schemaId];
      // The reagents may not have loaded yet for preexisting plate contents.
      if (assignmentGroup == null) {
        continue;
      }

      rowsContents.push(
        getSummaryViewRow({
          assignmentGroup,
          plateMaterialInputs,
          reagentData,
          schemaData,
          schemaGroupingId,
          schemaId,
          setContainerMergeSpan: groupIndex === 0 && Object.keys(reagentData.schemas).length > 1,
          showMaterialInputColumn,
        })
      );
      groupIndex++;
    }
  }
  return rowsContents;
}

/**
 * Gathers all the values, units, and errors for well quantity over the selected wells on the plate map.
 * If no wells are selected, considers the entire plate map. For values and units, either returns the unique
 * value/unit or returns a sentinel indicating that there are multiple distinct values/units present in the
 * plate map. For errors, returns the first error of each type (value error, units error) found in the plate
 * map.
 * @param plateMap The plate map
 * @param selectedWellIndices What indices in the plate are currently selected
 */
export function rollUpTotalWellQuantity(
  plateMap: PlateMap,
  selectedWellIndices: Set<number>
): MaybeMixedMeasurementWithErrors {
  const wellQuantityValues: Set<number | null> = new Set();
  const wellQuantityUnits: Set<string | null> = new Set();
  const errors: MeasurementValidationErrors = getDefaultMeasurementValidationErrors();
  for (const [wellIndex, well] of plateMap.wells.entries()) {
    if (selectedWellIndices.size > 0 && !selectedWellIndices.has(wellIndex)) {
      continue;
    }
    wellQuantityValues.add(well.finalQuantity.measurement.value);
    wellQuantityUnits.add(well.finalQuantity.measurement.unit?.symbol ?? null);
    errors.valueError ??= getCollatedCellError(errors.valueError, well.finalQuantity.errors.valueError);
    errors.unitsError ??= getCollatedCellError(errors.unitsError, well.finalQuantity.errors.unitsError);
  }

  const value: MaybeMixedMeasurementValue =
    // If we have all the same value, take the first (only) deduped value
    wellQuantityValues.size === 1 ? [...wellQuantityValues][0] : MULTIPLE_VALUES_SENTINEL;
  // If we have all the same unit, take the value from the first selected well, or from the first well
  // if no wells are selected (and therefore all wells were rolled up)
  const wellIndexForUnit = selectedWellIndices.size === 0 ? 0 : [...selectedWellIndices][0];
  const unit: MaybeMixedMeasurementUnit =
    wellQuantityUnits.size === 1
      ? plateMap.wells[wellIndexForUnit].finalQuantity.measurement.unit
      : MULTIPLE_VALUES_SENTINEL;
  return {measurement: {value, unit}, errors};
}

/**
 * Gets the coerced MaybeMixedMeasurement value from the contents of the value and unit table cells.
 */
function getMaybeMixedMeasurementFromCells(valueCell: Cell, unitCell: Cell): MaybeMixedMeasurement {
  let maybeMixedValue: MaybeMixedMeasurementValue;
  if (isMultipleValuesCell(valueCell)) {
    maybeMixedValue = MULTIPLE_VALUES_SENTINEL;
  } else {
    maybeMixedValue = coerceNumericValue(valueCell);
  }

  let maybeMixedUnit: MaybeMixedMeasurementUnit = null;
  if (unitCell.typedValue != null && unitCell.typedValue.type === BaseTypedValueType.UNIT_REFERENCE) {
    maybeMixedUnit = readFragmentFromCache(MeasurementUnitFragment, unitCell.typedValue.unitSummary.id);
  } else if (isMultipleValuesCell(unitCell)) {
    maybeMixedUnit = MULTIPLE_VALUES_SENTINEL;
  }
  return {value: maybeMixedValue, unit: maybeMixedUnit};
}

/**
 * Given a 'new' measurement that may represent multiple values, and an 'old' measurement, return a new
 * Measurement that contains the value / units of the 'new' measurement, unless they represent multiple
 * values, in which case return the value / units of the 'old' measurement
 * @param newMeasurement A measurement which may represent multiple values.
 * @param oldMeasurement A measurement to be updated.
 */
function clobberNonMixedValuesInMeasurement(
  newMeasurement: MaybeMixedMeasurement,
  oldMeasurement: Measurement
): Measurement {
  return {
    value: clobberNonMixedValue(newMeasurement.value, oldMeasurement.value),
    unit: clobberNonMixedValue(newMeasurement.unit, oldMeasurement.unit),
  };
}

/**
 * Given a 'new' scalar that may represent multiple values, and an 'old' scalar, return a new scalar with the
 * value of the 'new' scalar, unless it represents multiple values, in which case return the value of the
 * 'old' scalar.
 * @param newValue A scalar which may represent multiple values.
 * @param oldValue A scalar to be updated.
 */
function clobberNonMixedValue<T>(newValue: MaybeMixedValue<T>, oldValue: T | null): T | null {
  return newValue === MULTIPLE_VALUES_SENTINEL ? oldValue : newValue;
}
